import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, StatusBadge } from "@/components/shared/Common";
import { FilesPanel, StatusTimeline } from "@/components/shared/Records";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { Pick } from "@/pages/gerencia/Sales";
import { CATS, PRIOS, T_STATUS } from "./Tickets";

const STAFF_ACTIONS = { abierto: [["en_atencion", "Tomar ticket"], ["resuelto", "Marcar resuelto"]], reabierto: [["en_atencion", "Tomar ticket"], ["resuelto", "Marcar resuelto"]],
  en_atencion: [["resuelto", "Marcar resuelto"], ["abierto", "Devolver a abierto"]], resuelto: [["cerrado", "Cerrar"]] };
const OWNER_ACTIONS = { resuelto: [["cerrado", "Conforme, cerrar"], ["reabierto", "No resuelto, reabrir"]] };

const Info = ({ t, staff, onAssign }) => {
  const [list, setList] = useState([]);
  useEffect(() => { if (staff) api.get("/support/staff").then((r) => setList([{ id: "none", name: "Sin asignar" }, ...r.data])); }, [staff]);
  const rows = [["Categoría", CATS.find((c) => c.id === t.category)?.name], ["Prioridad", PRIOS.find((p) => p.id === t.priority)?.name], ["Creado por", t.created_by_name],
    ["Creado", fmtDateTime(t.created_at)], ["Primera respuesta", t.first_response_at ? fmtDateTime(t.first_response_at) : "—"],
    ["Resuelto", t.resolved_at ? fmtDateTime(t.resolved_at) : "—"], ["Cerrado", t.closed_at ? fmtDateTime(t.closed_at) : "—"]];
  return (
    <div className="syc-card p-5 space-y-2 text-sm" data-testid="ticket-info">
      {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3"><span className="text-[#5b6656]">{k}</span><b className="text-right">{v}</b></div>)}
      <div className="pt-2">
        <div className="text-[#5b6656] mb-1">Atendido por</div>
        {staff && t.status !== "cerrado" ? <Pick testid="ticket-assign-select" value={t.assigned_to || "none"} onChange={(v) => onAssign(v === "none" ? null : v)} options={list} />
          : <b data-testid="ticket-assigned">{t.assigned_to_name || "Sin asignar"}</b>}
      </div>
    </div>
  );
};

const Thread = ({ t, onReply }) => {
  const [text, setText] = useState("");
  const send = async () => { try { await api.post(`/support/tickets/${t.id}/messages`, { body: text }); setText(""); onReply(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="syc-card overflow-hidden" data-testid="ticket-thread">
      <div className="p-5 border-b border-[#e4e8e1]"><div className="text-xs text-[#8a9584] mb-1">{t.created_by_name} · {fmtDateTime(t.created_at)}</div><div className="whitespace-pre-wrap text-sm">{t.description}</div></div>
      {t.messages.map((m) => (
        <div key={m.id} data-testid={`ticket-msg-${m.id}`} className={`px-5 py-3 border-b border-[#f0f2ee] ${m.staff ? "bg-[#f6fbf2]" : ""}`}>
          <div className="text-xs text-[#8a9584] mb-1"><b className={m.staff ? "text-[#258F00]" : "text-[#1a2416]"}>{m.user_name}{m.staff ? " · Soporte" : ""}</b> · {fmtDateTime(m.created_at)}</div>
          <div className="whitespace-pre-wrap text-sm">{m.body}</div>
        </div>))}
      {t.status !== "cerrado" && <div className="p-4 flex gap-2 items-end">
        <Textarea data-testid="ticket-reply-input" rows={2} placeholder="Escriba una respuesta…" value={text} onChange={(e) => setText(e.target.value)} />
        <Button data-testid="ticket-reply-btn" className="rounded-full" disabled={!text.trim()} onClick={send}>Responder</Button>
      </div>}
    </div>
  );
};

export default function TicketDetail() {
  const { id } = useParams();
  const [t, setT] = useState(null);
  const [note, setNote] = useState("");
  const load = () => api.get(`/support/tickets/${id}`).then((r) => setT(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!t) return null;
  const actions = [...(t.can_attend ? STAFF_ACTIONS[t.status] || [] : []), ...(t.is_owner ? OWNER_ACTIONS[t.status] || [] : [])].filter((a, i, arr) => arr.findIndex((b) => b[0] === a[0]) === i);
  const move = async (to) => { try { await api.post(`/support/tickets/${id}/transition`, { to, note }); setNote(""); toast.success(T_STATUS[to][1]); load(); } catch (e) { toast.error(errMsg(e)); } };
  const assign = async (uid) => { try { await api.put(`/support/tickets/${id}/assign`, { user_id: uid }); load(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div data-testid="ticket-detail-page">
      <Link to="/soporte" className="inline-flex items-center gap-1 text-sm text-[#258F00] mb-3" data-testid="ticket-back-link"><ArrowLeft size={15} />Soporte</Link>
      <PageHeader eyebrow={t.code} title={t.title} actions={<StatusBadge testid="ticket-status" status={T_STATUS[t.status][0]} label={T_STATUS[t.status][1]} />} />
      {actions.length > 0 && <div className="syc-card p-4 mb-5 flex flex-wrap gap-2 items-end" data-testid="ticket-actions">
        <Textarea data-testid="ticket-note-input" rows={1} className="flex-1 min-w-[220px]" placeholder="Nota del cambio de estado (obligatoria para reabrir)" value={note} onChange={(e) => setNote(e.target.value)} />
        {actions.map(([to, label]) => <Button key={to} data-testid={`ticket-action-${to}`} variant={to === "reabierto" || to === "abierto" ? "outline" : "default"} className="rounded-full" onClick={() => move(to)}>{label}</Button>)}
      </div>}
      <div className="grid lg:grid-cols-[1fr_320px] gap-5">
        <div className="space-y-5 min-w-0"><Thread t={t} onReply={load} /><FilesPanel resourceType="ticket" resourceId={t.id} canWrite={t.status !== "cerrado"} /></div>
        <div className="space-y-5"><Info t={t} staff={t.can_attend} onAssign={assign} /><StatusTimeline items={t.history} labels={Object.fromEntries(Object.entries(T_STATUS).map(([k, v]) => [k, v[1]]))} /></div>
      </div>
    </div>
  );
}
